import React, { useEffect, useMemo, useState } from 'react';
import DefaultProfileImage from '@assets/icons/default-profile-icon.svg';
import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Typography,
  Box,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import { useLanguage } from 'Context/LanguageContext';
import billingConst from 'FielderUtils/roleManagementConst/billingConst';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import { removeSession, getCognitoUser, removeCognitoUser } from 'FielderUtils/session/Session';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isLocalUser } from 'Utils/account/utils';
import { ENDPOINTS } from '../../../../core/constants/pageEndpoints.constants';
import { selectUser } from '../../../../core/store/modules/session/sessionSelectors';
import { setLogOutUser } from '../../../../core/store/modules/session/sessionSlice';
import useValidateRoles from '../../../../core/utils/roles/useValidateRoles';
import { isClient } from '@core/utils/roles/isRole';
import { useSmartTheme } from '@core/theme/SmartThemeProvider';
import { SmartSwitch } from '~/FielderElements/SmartTaskTable/SmartStyles';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';

export default function TopMenuProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lan = useLanguage();
  const user = useSelector(selectUser);
  const { isRoleValid } = useValidateRoles();
  const { mode, toggleMode } = useSmartTheme();
  const [profileImage, setProfileImage] = useState(DefaultProfileImage);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const isSuperAdminOrViewer = RoleUtils.isUserSuperAndSuperViewerAndAdministrator(user);
  const isInternalAdmin = RoleUtils.isInternalAdminUser(user);
  const isManager = RoleUtils.isUserManager(user);
  const localUser = isLocalUser(user);

  useEffect(() => {
    if (user?.profileImage?.scalePath) {
      setProfileImage(user.profileImage.scalePath);
    }
  }, [user]);
  const isDarkMode = mode === 'dark';
  const darkModeLabel = lan.darkMode ?? 'Dark';
  const validateChangePassword = useMemo(() => {
    return (
      RoleUtils.isRootGlobalAccountUser(user) ||
      RoleUtils.isClientGlobalAccountUser(user) ||
      (!isInternalAdmin && user && user.users == null) ||
      isSuperAdminOrViewer
    );
  }, [user, isInternalAdmin, isSuperAdminOrViewer]);
  const handleToggleTheme = (event) => {
    event?.stopPropagation?.();
    toggleMode();
  };
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const go = (path) => () => {
    handleClose();
    navigate(path);
  };
  const logOut = () => {
    const storedCognitoUser = getCognitoUser();
    if (storedCognitoUser) {
      handleLogoutCognito();
    } else {
      dispatch(setLogOutUser());
      removeSession();
      navigate(ENDPOINTS.login);
    }
  };
  const handleLogoutCognito = async () => {
    const domain = process.env.COGNITO_DOMAIN_NAME;
    const clientId = process.env.COGNITO_CLIENT_ID;
    const logoutRedirectUri = encodeURIComponent(process.env.COGNITO_REDIRECT_LOGOUT || '');
    if (!domain || !clientId || !logoutRedirectUri) {
      console.error('Missing environment variables for logout!');
      return;
    }
    try {
      dispatch(setLogOutUser());
      sessionStorage.clear();
      removeCognitoUser();
      removeSession();
      window.location.href = `${domain}/logout?client_id=${clientId}&logout_uri=${logoutRedirectUri}`;
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };
  const fullName = user ? `${user.name ?? ''} ${user.lastName ?? ''}`.trim() : '';
  return (
    <>
      <Button
        id="profile-menu-button"
        aria-controls={menuOpen ? 'profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? 'true' : undefined}
        onClick={handleOpen}
        endIcon={
          <Avatar
            src={profileImage}
            sx={{ width: 28, height: 28, ml: 1 }}
            alt={fullName || 'profile'}
          />
        }
        sx={{
          textTransform: 'none',
          fontFamily: 'Lato, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 1.19,
          px: 1,
          minWidth: 0,
        }}
      >
        {fullName}
      </Button>
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        slotProps={{
          list: {
            dense: true,
          },
        }}
      >
        {!isInternalAdmin && localUser && (
          <MenuItem id="profile" onClick={go(ENDPOINTS.profile)}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            {lan.myProfile}
          </MenuItem>
        )}

        {validateChangePassword && (
          <MenuItem id="changePassword" onClick={go(ENDPOINTS.changePassword)}>
            <ListItemIcon>
              <LockResetIcon fontSize="small" />
            </ListItemIcon>
            {lan.changePassword}
          </MenuItem>
        )}

        {!isInternalAdmin && localUser && (
          <MenuItem id="configuration" onClick={go(ENDPOINTS.configuration)}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            {lan.configuration}
          </MenuItem>
        )}
        {!isInternalAdmin &&
          isSuperAdminOrViewer &&
          isRoleValid(billingConst.BILLING, billingConst.VIEW) && (
            <MenuItem id="billingSummary" onClick={go(ENDPOINTS.billingSummary)}>
              <ListItemIcon>
                <ReceiptLongIcon fontSize="small" />
              </ListItemIcon>
              {lan.billing}
            </MenuItem>
          )}
        {((!isInternalAdmin && isSuperAdminOrViewer) || isManager || isClient(user)) && (
          <MenuItem id="support" onClick={go(ENDPOINTS.support)}>
            <ListItemIcon>
              <SupportAgentIcon fontSize="small" />
            </ListItemIcon>
            {lan.support}
          </MenuItem>
        )}
        <MenuItem
          id="toggleTheme"
          disableRipple
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <ListItemIcon>
              <DarkModeOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">{darkModeLabel}</Typography>
          </Box>
          <SmartSwitch
            checked={isDarkMode}
            onChange={handleToggleTheme}
          />
        </MenuItem>
        {!isInternalAdmin && !localUser && (
          <MenuItem
            id="globalDashboard"
            onClick={() => {
              handleClose();
              if (RoleUtils.isRootGlobalAccountUser(user)) {
                navigate(ENDPOINTS.globalAccountDashboard);
              } else {
                navigate(ENDPOINTS.globalAccountClientDashboard);
              }
            }}
          >
            <ListItemIcon>
              <DashboardIcon fontSize="small" />
            </ListItemIcon>
            {lan.globalDashboard}
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          id="logout"
          onClick={() => {
            handleClose();
            logOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {lan.logOut}
        </MenuItem>
      </Menu>
    </>
  );
}
//Original code:
/*import React, { useEffect, useMemo, useState } from 'react';
import DefaultProfileImage from '@assets/icons/default-profile-icon.svg';
import { Avatar, Button, Menu, MenuItem, Divider, ListItemIcon, } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import { useLanguage } from 'Context/LanguageContext';
import billingConst from 'FielderUtils/roleManagementConst/billingConst';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import { removeSession, getCognitoUser, removeCognitoUser } from 'FielderUtils/session/Session';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isLocalUser } from 'Utils/account/utils';
import { ENDPOINTS } from '../../../../core/constants/pageEndpoints.constants';
import { selectUser } from '../../../../core/store/modules/session/sessionSelectors';
import { setLogOutUser } from '../../../../core/store/modules/session/sessionSlice';
import useValidateRoles from '../../../../core/utils/roles/useValidateRoles';
import { isClient } from '@core/utils/roles/isRole';
import { useSmartTheme } from '@core/theme/SmartThemeProvider';
import { DarkMode, LightMode } from '@mui/icons-material';

export default function TopMenuProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lan = useLanguage();
  const user = useSelector(selectUser);
  const { isRoleValid } = useValidateRoles();
  const { mode, toggleMode } = useSmartTheme();
  const [profileImage, setProfileImage] = useState(DefaultProfileImage);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const isSuperAdminOrViewer = RoleUtils.isUserSuperAndSuperViewerAndAdministrator(user);
  const isInternalAdmin = RoleUtils.isInternalAdminUser(user);
  const isManager = RoleUtils.isUserManager(user);
  const localUser = isLocalUser(user);
  useEffect(() => {
    if (user?.profileImage?.scalePath) setProfileImage(user.profileImage.scalePath);
  }, [user]);
  const isDarkMode = mode === 'dark';
  const darkModeLabel = lan.darkMode ?? 'Dark mode';
  const lightModeLabel = lan.lightMode ?? 'Light mode';
  const validateChangePassword = useMemo(() => {
    return (
      RoleUtils.isRootGlobalAccountUser(user) ||
      RoleUtils.isClientGlobalAccountUser(user) ||
      (!isInternalAdmin && user && user.users == null) ||
      isSuperAdminOrViewer
    );
  }, [user, isInternalAdmin, isSuperAdminOrViewer]);
  const handleToggleTheme = () => {
    toggleMode();
    handleClose();
  };
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const go = (path) => () => {
    handleClose();
    navigate(path);
  };
  const logOut = () => {
    const storedCognitoUser = getCognitoUser();
    if (storedCognitoUser) {
      handleLogoutCognito();
    } else {
      dispatch(setLogOutUser());
      removeSession();
      navigate(ENDPOINTS.login);
    }
  };
  const handleLogoutCognito = async () => {
    const domain = process.env.COGNITO_DOMAIN_NAME;
    const clientId = process.env.COGNITO_CLIENT_ID;
    const logoutRedirectUri = encodeURIComponent(process.env.COGNITO_REDIRECT_LOGOUT || '');

    if (!domain || !clientId || !logoutRedirectUri) {
      console.error('Missing environment variables for logout!');
      return;
    }
    try {
      dispatch(setLogOutUser());
      sessionStorage.clear();
      removeCognitoUser();
      removeSession();
      window.location.href = `${domain}/logout?client_id=${clientId}&logout_uri=${logoutRedirectUri}`;
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };
  const fullName = user ? `${user.name ?? ''} ${user.lastName ?? ''}`.trim() : '';
  return (
    <>
<Button
  id="profile-menu-button"
  aria-controls={menuOpen ? 'profile-menu' : undefined}
  aria-haspopup="true"
  aria-expanded={menuOpen ? 'true' : undefined}
  onClick={handleOpen}
  endIcon={
    <Avatar
      src={profileImage}
      sx={{ width: 28, height: 28, ml: 1 }}
      alt={fullName || 'profile'}
    />
  }
  sx={{
    textTransform: 'none',
    fontFamily: 'Lato, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.19,
    px: 1,
    minWidth: 0,
  }}
>
  {fullName}
</Button>
<Menu
  id="profile-menu"
  anchorEl={anchorEl}
  open={menuOpen}
  onClose={handleClose}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
  keepMounted
  MenuListProps={{ dense: true }}
>
  {!isInternalAdmin && localUser && (
    <MenuItem id="profile" onClick={go(ENDPOINTS.profile)}>
      <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
      {lan.myProfile}
    </MenuItem>
  )}
  {validateChangePassword && (
    <MenuItem id="changePassword" onClick={go(ENDPOINTS.changePassword)}>
      <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
      {lan.changePassword}
    </MenuItem>
  )}
  {!isInternalAdmin && localUser && (
    <MenuItem id="configuration" onClick={go(ENDPOINTS.configuration)}>
      <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
      {lan.configuration}
    </MenuItem>
  )}
  {!isInternalAdmin &&
    isSuperAdminOrViewer &&
    isRoleValid(billingConst.BILLING, billingConst.VIEW) && (
      <MenuItem id="billingSummary" onClick={go(ENDPOINTS.billingSummary)}>
        <ListItemIcon><ReceiptLongIcon fontSize="small" /></ListItemIcon>
        {lan.billing}
      </MenuItem>
    )
  }
  {((!isInternalAdmin && isSuperAdminOrViewer) || isManager || isClient(user)) && (
    <MenuItem id="support" onClick={go(ENDPOINTS.support)}>
      <ListItemIcon><SupportAgentIcon fontSize="small" /></ListItemIcon>
      {lan.support}
    </MenuItem>
  )}
  <MenuItem id="toggleTheme" onClick={handleToggleTheme}>
    <ListItemIcon>
      {isDarkMode ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
    </ListItemIcon>
    {isDarkMode ? lightModeLabel : darkModeLabel}
  </MenuItem>
  {!isInternalAdmin && !localUser && (
    <MenuItem
      id="globalDashboard"
      onClick={() => {
        handleClose();
        if (RoleUtils.isRootGlobalAccountUser(user)) {
          navigate(ENDPOINTS.globalAccountDashboard);
        } else {
          navigate(ENDPOINTS.globalAccountClientDashboard);
        }
      }}
    >
      <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
      {lan.globalDashboard}
    </MenuItem>
  )}
  <Divider sx={{ my: 0.5 }} />
  <MenuItem id="logout" onClick={() => { handleClose(); logOut(); }}>
    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
    {lan.logOut}
  </MenuItem>
</Menu>
</>
);
}
*/
